import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';

const PermissionMatrix = ({ user, permissions, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedPermissions, setEditedPermissions] = useState(permissions);

  const modules = [
    {
      name: 'Shipment Register',
      key: 'shipment',
      permissions: ['view', 'create', 'edit', 'delete', 'export'],
    },
    {
      name: 'Cost Register',
      key: 'cost',
      permissions: ['view', 'create', 'edit', 'delete', 'export'],
    },
    {
      name: 'Fee Register',
      key: 'fee',
      permissions: ['view', 'create', 'edit', 'delete', 'export'],
    },
    {
      name: 'Issue & Risk Log',
      key: 'issue',
      permissions: ['view', 'create', 'edit', 'delete', 'export'],
    },
    {
      name: 'Monthly Review Notes',
      key: 'review',
      permissions: ['view', 'create', 'edit', 'delete', 'export'],
    },
    {
      name: 'User Management',
      key: 'user',
      permissions: ['view', 'create', 'edit', 'delete'],
    },
    {
      name: 'Master Data',
      key: 'master',
      permissions: ['view', 'create', 'edit', 'delete'],
    },
    {
      name: 'Reports & Analytics',
      key: 'reports',
      permissions: ['view', 'export'],
    },
    {
      name: 'Accounts Payable',
      key: 'accountsPayable',
      permissions: ['view', 'create', 'edit', 'approve', 'export'],
    },
    {
      name: 'Accounts Receivable',
      key: 'accountsReceivable',
      permissions: ['view', 'create', 'edit', 'approve', 'export'],
    },
    {
      name: 'Invoices',
      key: 'invoices',
      permissions: ['view', 'create', 'edit', 'approve', 'export'],
    },
    {
      name: 'Bank Reconciliation',
      key: 'bankReconciliation',
      permissions: ['view', 'create', 'edit', 'export'],
    },
    {
      name: 'Exchange Rates',
      key: 'exchangeRates',
      permissions: ['view', 'create', 'edit', 'export'],
    },
    {
      name: 'Trading Operations',
      key: 'tradingOperations',
      permissions: ['view', 'create', 'edit', 'export'],
    },
    {
      name: 'Operations Management',
      key: 'operationsManagement',
      permissions: ['view', 'create', 'edit', 'export'],
    },
  ];

  const permissionLabels = {
    view: 'View',
    create: 'Create',
    edit: 'Edit',
    delete: 'Delete',
    approve: 'Approve',
    export: 'Export',
  };

  const handlePermissionToggle = (moduleKey, permission) => {
    setEditedPermissions((prev) => ({
      ...prev,
      [moduleKey]: {
        ...prev?.[moduleKey],
        [permission]: !prev?.[moduleKey]?.[permission],
      },
    }));
  };

  const handleSelectAll = (moduleKey) => {
    const module = modules?.find((m) => m?.key === moduleKey);
    const allSelected = module.permissions?.every(
      (p) => editedPermissions?.[moduleKey]?.[p]
    );

    setEditedPermissions((prev) => ({
      ...prev,
      [moduleKey]: module.permissions?.reduce(
        (acc, p) => ({ ...acc, [p]: !allSelected }),
        {}
      ),
    }));
  };

  const handleSave = () => {
    onUpdate(user?.id, editedPermissions);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedPermissions(permissions);
    setIsEditing(false);
  };

  return (
    <div className="bg-card rounded-lg border border-border">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              Permission Matrix
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Configure module-level access permissions for {user?.name}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!isEditing ? (
              <Button
                variant="outline"
                size="sm"
                iconName="Edit"
                onClick={() => setIsEditing(true)}
              >
                Edit Permissions
              </Button>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  iconName="Save"
                  onClick={handleSave}
                >
                  Save Changes
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="p-6">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">
                  Module
                </th>
                {Object.entries(permissionLabels)?.map(([key, label]) => (
                  <th
                    key={key}
                    className="text-center py-3 px-4 text-sm font-semibold text-foreground"
                  >
                    {label}
                  </th>
                ))}
                <th className="text-center py-3 px-4 text-sm font-semibold text-foreground">
                  All
                </th>
              </tr>
            </thead>
            <tbody>
              {modules?.map((module) => (
                <tr
                  key={module.key}
                  className="border-b border-border hover:bg-accent/5"
                >
                  <td className="py-3 px-4 text-sm font-medium text-foreground">
                    {module.name}
                  </td>
                  {Object.keys(permissionLabels)?.map((permission) => (
                    <td key={permission} className="text-center py-3 px-4">
                      {module.permissions?.includes(permission) ? (
                        <Checkbox
                          checked={
                            editedPermissions?.[module.key]?.[permission] || false
                          }
                          onChange={() =>
                            handlePermissionToggle(module.key, permission)
                          }
                          disabled={!isEditing}
                        />
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  ))}
                  <td className="text-center py-3 px-4">
                    <button
                      onClick={() => handleSelectAll(module.key)}
                      disabled={!isEditing}
                      className="text-primary hover:text-primary/80 disabled:text-muted-foreground disabled:cursor-not-allowed"
                    >
                      <Icon name="CheckSquare" size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 p-4 bg-muted/30 rounded-lg">
          <div className="flex items-start gap-3">
            <Icon
              name="Info"
              size={20}
              className="text-primary flex-shrink-0 mt-0.5"
            />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">
                Permission Guidelines:
              </p>
              <ul className="space-y-1 list-disc list-inside">
                <li>View: Read-only access to module data</li>
                <li>Create: Ability to add new records</li>
                <li>Edit: Modify existing records</li>
                <li>Delete: Remove records (soft delete with audit trail)</li>
                <li>Approve: Authority to approve transactions or payments</li>
                <li>Export: Download data in CSV/Excel/PDF formats</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionMatrix;